import React, { useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtDate = (v) => {
  if (!v) return "-";
  const s = String(v);
  // Fechas "solo dia" (CISA KEV) se formatean sin convertir zona horaria
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m && s.length <= 10) return `${Number(m[3])}/${Number(m[2])}/${m[1]}`;
  try {
    return new Date(s).toLocaleDateString("es-AR", { timeZone: "America/Argentina/Buenos_Aires" });
  } catch {
    return s;
  }
};

const KevThreatsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/threat-intel/kev/latest?limit=5`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="bg-white rounded-lg shadow-lg p-6" data-testid="kev-threats-panel">
      <div className="flex items-start justify-between mb-1">
        <h2 className="text-xl font-bold text-gray-900 flex items-center">
          <span className="w-8 h-8 mr-2 rounded-full bg-red-600 text-white text-sm font-bold flex items-center justify-center">
            !
          </span>
          Amenazas del Momento
        </h2>
        <span className="text-[11px] uppercase tracking-wide font-semibold text-red-700 bg-red-50 border border-red-200 px-2 py-1 rounded">
          Explotación activa
        </span>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        Últimas vulnerabilidades incorporadas al catálogo <strong>CISA KEV</strong> (explotación
        confirmada en el mundo real).
      </p>

      {loading && (
        <div className="py-10 text-center text-gray-500 text-sm" data-testid="kev-threats-loading">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto mb-3"></div>
          Consultando catálogo de amenazas...
        </div>
      )}

      {!loading && error && (
        <div className="py-8 text-center text-sm text-gray-500" data-testid="kev-threats-error">
          No se pudo cargar el catálogo de amenazas en este momento.
        </div>
      )}

      {!loading && !error && data && (
        <>
          {data.affected_count > 0 && (
            <div
              className="mb-4 flex items-center rounded-lg border border-red-300 bg-red-50 px-3 py-2"
              data-testid="kev-threats-affected-banner"
            >
              <span className="text-red-600 mr-2 text-lg leading-none">●</span>
              <p className="text-sm text-red-800">
                <strong>{data.affected_count}</strong> de estas amenazas coincide
                {data.affected_count === 1 ? "" : "n"} con vulnerabilidades detectadas en tu
                infraestructura.
              </p>
            </div>
          )}

          <div className="space-y-2" data-testid="kev-threats-list">
            {(data.items || []).length === 0 && (
              <p className="text-sm text-gray-500 py-6 text-center" data-testid="kev-threats-empty">
                El catálogo todavía no tiene entradas cargadas.
              </p>
            )}
            {(data.items || []).map((item) => (
              <div
                key={item.cve_id}
                className={`p-3 rounded-lg border transition-colors ${
                  item.affects_company
                    ? "border-red-300 bg-red-50"
                    : "border-gray-200 bg-gray-50 hover:bg-gray-100"
                }`}
                data-testid={`kev-threat-item-${item.cve_id}`}
              >
                <div className="flex items-center flex-wrap gap-2">
                  <a
                    href={`https://nvd.nist.gov/vuln/detail/${item.cve_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-sm font-bold text-blue-700 hover:underline"
                    data-testid={`kev-threat-link-${item.cve_id}`}
                  >
                    {item.cve_id}
                  </a>
                  {item.affects_company && (
                    <span
                      className="text-[10px] font-bold uppercase text-white bg-red-600 px-2 py-0.5 rounded"
                      data-testid={`kev-threat-affects-${item.cve_id}`}
                    >
                      Te afecta
                    </span>
                  )}
                  {item.ransomware && (
                    <span className="text-[10px] font-bold uppercase text-orange-800 bg-orange-100 border border-orange-200 px-2 py-0.5 rounded">
                      Ransomware
                    </span>
                  )}
                  <span className="text-xs text-gray-500 ml-auto">{fmtDate(item.date_added)}</span>
                </div>
                <p className="text-sm font-medium text-gray-800 mt-1">
                  {item.vendor} {item.product}
                </p>
                <p className="text-xs text-gray-600 line-clamp-2">
                  {item.vulnerability_name || item.short_description}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
            <span data-testid="kev-threats-catalog-count">
              {data.catalog_count} CVEs en el catálogo
              {data.catalog_version ? ` · v${data.catalog_version}` : ""}
            </span>
            <span>Actualizado: {fmtDate(data.refreshed_at)}</span>
          </div>
        </>
      )}
    </div>
  );
};

export default KevThreatsPanel;
